import { useEffect, useState } from 'react';
import { applyTableQuery, nextSort, type ColumnSort } from '../lib/list-query';
import { PageHead, SortHeader } from '../components/ui/chrome';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api-client';
import { Tabs } from '../components/ui/Badge';
import { useNotice } from '../components/ui/Notice';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { emptyBankAccount, type BankAccount } from '../lib/bank';
import type { EmailTemplate, EmailTemplateKind, SupplierTemplate } from '../lib/email-template';
import type { TermsTemplate, TermsTemplateKind } from '../lib/terms-template';
import { Field, Input, Select, TextArea } from '../components/ui/Input';
import { ChipList } from '../components/ChipList';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';

interface SyncFrom { hubspot?: string; clockify?: string; sharepoint?: string }

function toLondonInput(iso?: string): string {
  if (!iso) return '';
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(parsed));
  const read = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return `${read('year')}-${read('month')}-${read('day')}T${read('hour')}:${read('minute')}`;
}

function SyncFromField({ label, hint, value, onSave }: { label: string; hint: string; value?: string; onSave: (at: string) => Promise<void> }) {
  const [draft, setDraft] = useState(toLondonInput(value));
  useEffect(() => setDraft(toLondonInput(value)), [value]);
  return (
    <div className="mt-4">
      <Field label={label}><Input type="datetime-local" value={draft} onChange={(event) => setDraft(event.target.value)} /></Field>
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
      <Button className="mt-2" size="sm" variant="outline" onClick={() => void onSave(draft)}>Save sync time</Button>
    </div>
  );
}

interface CrunchStatus {
  clientConfigured: boolean;
  connected: boolean;
  email?: string;
  userName?: string;
  companyName?: string;
  lastError?: string;
  lastSyncAt?: string;
  yearStart?: string;
  yearEnd?: string;
  periodName?: string;
}

interface GroupRow { name: string; members: string[]; role: string }
interface JobRow { key: string; name: string; cadence: string; enabled: boolean }
type FolderKey = 'contracts' | 'nda' | 'purchaseOrders' | 'invoices' | 'quotations' | 'licences' | 'bills' | 'expenses';
type Tab = 'company' | 'people' | 'sharepoint' | 'integrations' | 'accounting' | 'jobs';

function initialTab(search: URLSearchParams): Tab {
  if (search.get('crunch')) return 'accounting';
  const requested = search.get('tab');
  if (requested === 'company' || requested === 'people' || requested === 'sharepoint' || requested === 'integrations' || requested === 'accounting' || requested === 'jobs') return requested;
  return 'company';
}

const FOLDER_LABELS: { key: FolderKey; label: string }[] = [
  { key: 'contracts', label: 'Contract' },
  { key: 'nda', label: 'NDA' },
  { key: 'purchaseOrders', label: 'Purchase orders' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'licences', label: 'Licences' },
];

const CONNECTION_ORDER = ['crunch', 'cursor', 'clockify', 'github', 'hubspot', 'graph', 'sharepoint', 'azure'] as const;

function connectionLabel(key: string, adapter: string): string {
  if (key === 'crunch') return `Accounting (${adapter === 'crunch' ? 'Crunch' : adapter})`;
  if (key === 'graph') return 'Microsoft';
  if (key === 'sharepoint') return 'SharePoint';
  if (key === 'github') return 'GitHub';
  if (key === 'hubspot') return 'HubSpot';
  if (key === 'clockify') return 'Clockify';
  if (key === 'cursor') return 'Cursor';
  if (key === 'azure') return 'Azure';
  return key;
}

function crunchReason(reason: string | null) {
  if (reason === 'missing_client') return 'Crunch client id and secret are not set on the API.';
  if (reason === 'denied') return 'Crunch did not grant access.';
  if (reason === 'token_exchange') return 'Crunch token exchange failed.';
  if (reason === 'invalid_state') return 'Crunch login state was invalid. Try Connect again.';
  return 'Crunch connect failed.';
}

export function SettingsPage() {
  const notice = useNotice();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(initialTab(params));
  const supplierSearch = useColumnSearch();
  const emailSearch = useColumnSearch();
  const termsSearch = useColumnSearch();
  const bankSearch = useColumnSearch();
  const jobColumnSearch = useColumnSearch();
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [supplierTemplates, setSupplierTemplates] = useState<SupplierTemplate[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [termsTemplates, setTermsTemplates] = useState<TermsTemplate[]>([]);
  const [currency, setCurrency] = useState('GBP');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [clockifyWorkspaceId, setClockifyWorkspaceId] = useState('');
  const [clockifyRegion, setClockifyRegion] = useState('global');
  const [sharePointSiteId, setSharePointSiteId] = useState('');
  const [sharePointSiteUrl, setSharePointSiteUrl] = useState('');
  const [sharePointDriveName, setSharePointDriveName] = useState('Documents');
  const [sharePointBasePath, setSharePointBasePath] = useState('01. Finance & Legal/Customers');
  const [folders, setFolders] = useState<Record<FolderKey, string>>({
    contracts: 'Contract', nda: 'NDA', purchaseOrders: 'Purchase Orders', invoices: 'Invoices', quotations: 'Quotations', licences: 'Licences', bills: 'Bills', expenses: 'Expenses',
  });
  const [graphMailbox, setGraphMailbox] = useState('');
  const [githubRepo, setGithubRepo] = useState('');
  const [connections, setConnections] = useState<Record<string, boolean>>({});
  const [secretStatus, setSecretStatus] = useState<{ id: string; label: string; source: 'settings' | 'server' | 'none' }[]>([]);
  const [secretDraft, setSecretDraft] = useState<Record<string, string>>({});
  const [endpoints, setEndpoints] = useState<{ id: string; group: string; label: string; value: string }[]>([]);
  const [endpointDraft, setEndpointDraft] = useState<Record<string, string>>({});
  const [crunchClientId, setCrunchClientId] = useState('');
  const [accountingAdapter, setAccountingAdapter] = useState('crunch');
  const [crunch, setCrunch] = useState<CrunchStatus>({ clientConfigured: false, connected: false });
  const [syncFrom, setSyncFrom] = useState<SyncFrom>({});
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [supplierEdit, setSupplierEdit] = useState<{ index: number | null; draft: SupplierTemplate } | null>(null);
  const [termsEdit, setTermsEdit] = useState<{ index: number | null; draft: TermsTemplate } | null>(null);
  const [emailEdit, setEmailEdit] = useState<{ index: number | null; draft: EmailTemplate } | null>(null);
  const [bankEdit, setBankEdit] = useState<{ index: number | null; draft: BankAccount } | null>(null);
  const [jobSearch, setJobSearch] = useState('');
  const [jobSort, setJobSort] = useState<ColumnSort | null>(null);

  function apply(data: { settings?: Record<string, unknown>; connections?: Record<string, boolean>; secrets?: { id: string; label: string; source: 'settings' | 'server' | 'none' }[]; endpoints?: { id: string; group: string; label: string; value: string }[]; crunch?: CrunchStatus; groups?: GroupRow[]; jobs?: JobRow[] }) {
    const s = data.settings || {};
    const folder = (s.sharePointFolders || {}) as Partial<Record<FolderKey, string>>;
    setCompanyName(String(s.companyName || ''));
    setCompanyNumber(String(s.companyNumber || ''));
    setVatNumber(String(s.vatNumber || ''));
    setCompanyAddress(String(s.companyAddress || ''));
    setBillingEmail(String(s.billingEmail || ''));
    setSupplierTemplates(Array.isArray(s.supplierTemplates) ? s.supplierTemplates as SupplierTemplate[] : []);
    setEmailTemplates(Array.isArray(s.emailTemplates) ? s.emailTemplates as EmailTemplate[] : []);
    setTermsTemplates(Array.isArray(s.termsTemplates) ? s.termsTemplates as TermsTemplate[] : []);
    setCurrency(String(s.currency || 'GBP'));
    setBankAccounts(Array.isArray(s.bankAccounts) ? (s.bankAccounts as BankAccount[]) : []);
    setYearStart(String(s.yearStart || ''));
    setYearEnd(String(s.yearEnd || ''));
    setClockifyWorkspaceId(String(s.clockifyWorkspaceId || ''));
    setClockifyRegion(String(s.clockifyRegion || 'global'));
    setSharePointSiteId(String(s.sharePointSiteId || ''));
    setSharePointSiteUrl(String(s.sharePointSiteUrl || ''));
    setSharePointDriveName(String(s.sharePointDriveName || 'Documents'));
    setSharePointBasePath(String(s.sharePointBasePath || '01. Finance & Legal/Customers'));
    setFolders({
      contracts: folder.contracts || 'Contract',
      nda: folder.nda || 'NDA',
      purchaseOrders: folder.purchaseOrders || 'Purchase Orders',
      invoices: folder.invoices || 'Invoices',
      quotations: folder.quotations || 'Quotations',
      licences: folder.licences || 'Licences',
      bills: folder.bills || 'Bills',
      expenses: folder.expenses || 'Expenses',
    });
    setGraphMailbox(String(s.graphMailbox || ''));
    setGithubRepo(String(s.githubRepo || ''));
    setCrunchClientId(String(s.crunchClientId || ''));
    setAccountingAdapter(String(s.accountingAdapter || 'crunch'));
    setSecretStatus(data.secrets || []);
    setSecretDraft({});
    setEndpoints(data.endpoints || []);
    setEndpointDraft(Object.fromEntries((data.endpoints || []).map((row) => [row.id, row.value])));
    setConnections(data.connections || {});
    setCrunch(data.crunch || { clientConfigured: false, connected: false });
    setSyncFrom((s.syncFrom || {}) as SyncFrom);
    setGroups((data.groups || []).map((group) => ({ name: group.name, role: group.role, members: [...(group.members || [])] })));
    if (data.jobs) setJobs(data.jobs.map((job) => ({ ...job })));
  }

  function reload() {
    void apiGet<Parameters<typeof apply>[0]>('/api/settings').then(apply);
  }

  useEffect(() => {
    const crunchParam = params.get('crunch');
    if (crunchParam === 'connected') notice.success('Crunch connected');
    else if (crunchParam === 'error') notice.failure(crunchReason(params.get('reason')));
    reload();
  }, []);

  function payload() {
    return {
      companyName, companyNumber, vatNumber, companyAddress, billingEmail, supplierTemplates, emailTemplates, termsTemplates, currency, yearStart, yearEnd, bankAccounts,
      clockifyWorkspaceId, clockifyRegion, sharePointSiteId, sharePointSiteUrl, sharePointDriveName, sharePointBasePath,
      sharePointFolders: folders, graphMailbox, githubRepo, accountingAdapter, jobs,
    };
  }

  const syncHint = 'The next sync reads from this time until it starts. Set it earlier to fetch that range again. Matching rows are updated.';

  async function saveSyncTime(source: 'crunch' | 'hubspot' | 'clockify' | 'sharepoint', at: string) {
    await notice.run('Saving sync time', async () => {
      const result = await apiPut<{ crunch?: CrunchStatus; settings?: { syncFrom?: SyncFrom } }>('/api/integrations/sync-from', { source, at });
      if (result.crunch) setCrunch(result.crunch);
      if (result.settings?.syncFrom) setSyncFrom(result.settings.syncFrom);
    }, 'Sync time saved', { confirm: false });
  }

  async function save() {
    await notice.run('Saving settings', async () => {
      await apiPut('/api/settings', payload());
      reload();
    }, 'Saved', { confirm: false });
  }

  async function saveConnection() {
    await notice.run('Saving connection', async () => {
      const result = await apiPost<Parameters<typeof apply>[0]>('/api/integrations/secrets', { secrets: secretDraft, endpoints: endpointDraft, crunchClientId });
      apply(result);
    }, 'Connection saved', { confirm: false });
  }

  async function saveService() {
    await notice.run('Saving', async () => {
      await apiPut('/api/settings', payload());
      const result = await apiPost<Parameters<typeof apply>[0]>('/api/integrations/secrets', { secrets: secretDraft, endpoints: endpointDraft, crunchClientId });
      apply(result);
    }, 'Saved', { confirm: false });
  }

  function endpointFields(group: string) {
    return endpoints.filter((row) => row.group === group).map((row) => (
      <Field key={row.id} label={row.label}>
        <Input value={endpointDraft[row.id] ?? row.value} autoComplete="off" placeholder={row.id === 'clockifyReportBase' ? 'Leave blank to use the region' : ''} onChange={(event) => setEndpointDraft((current) => ({ ...current, [row.id]: event.target.value }))} />
      </Field>
    ));
  }

  function secretFields(group: string) {
    return secretStatus.filter((row) => row.id === group.toLowerCase() || (group === 'Microsoft' && row.id === 'microsoft')).map((row) => (
      <Field key={row.id} label={row.label}>
        <Input type="password" autoComplete="new-password" value={secretDraft[row.id] || ''} placeholder={row.source === 'none' ? 'Not set' : 'Leave blank to keep the current key'} onChange={(event) => setSecretDraft((current) => ({ ...current, [row.id]: event.target.value }))} />
        <p className="mt-1 text-xs text-[var(--bo-faint)]">{row.source === 'settings' ? 'Saved in settings' : row.source === 'server' ? 'Using the server key' : 'Not set'}</p>
      </Field>
    ));
  }

  function preview(kind: FolderKey) {
    const root = sharePointBasePath.replace(/^\/+|\/+$/g, '');
    const folder = folders[kind].replace(/^\/+|\/+$/g, '');
    return [sharePointDriveName || 'Documents', root, 'RWG', folder].filter(Boolean).join(' / ');
  }

  return (
    <div>
      <PageHead crumbs={[{ label: 'Settings' }]} title="Settings" subtitle="Company, users, SharePoint, integrations, and accounting." />
      <Tabs tabs={[{ id: 'company', label: 'Company' }, { id: 'people', label: 'Users' }, { id: 'sharepoint', label: 'SharePoint' }, { id: 'integrations', label: 'Integrations' }, { id: 'accounting', label: 'Accounting' }, { id: 'jobs', label: 'Jobs' }]} value={tab} onChange={(id) => setTab(id as Tab)} />
      {tab === 'company' ? (
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Company name"><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></Field>
          <Field label="Company number"><Input value={companyNumber} onChange={(e) => setCompanyNumber(e.target.value)} /></Field>
          <Field label="VAT number"><Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} /></Field>
          <Field label="Address"><Input value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} /></Field>
          <Field label="Billing email"><Input value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} /></Field>
          <Field label="Currency"><Select value={currency === 'EUR' || currency === 'USD' ? currency : 'GBP'} onChange={(e) => setCurrency(e.target.value)}><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="USD">USD</option></Select></Field>
          </div>
          <Button className="mt-4 w-fit" onClick={() => void save()}>Save company</Button>
        </div>
      ) : null}
      {tab === 'company' ? (
        <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Supplier details</h2>
            <Button variant="outline" onClick={() => setSupplierEdit({ index: null, draft: { id: crypto.randomUUID(), name: '', contactName: '', contactRole: '', contactEmail: '', contactPhone: '' } })}>Add template</Button>
          </div>
          <p className="text-sm text-gray-500">These are the contacts printed on documents we send. A quotation, proposal, supplier purchase order, or invoice can pick one, then the fields can still be edited.</p>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Email</th><th className="px-4 py-3 text-right"><ColumnFilterToggle open={supplierSearch.open} onClick={supplierSearch.toggle} /></th></tr>
                {supplierSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Name' }, { key: 'contact', label: 'Contact' }, { key: 'email', label: 'Email' }]} draft={supplierSearch.draft} onChange={supplierSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {supplierTemplates.map((row, index) => ({ row, index })).filter(({ row }) => supplierSearch.matches({ name: row.name || 'Untitled', contact: row.contactName, email: row.contactEmail })).map(({ row, index }) => (
                  <tr key={row.id} className="cursor-pointer border-t border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800" onClick={() => setSupplierEdit({ index, draft: { ...row } })}>
                    <td className="px-4 py-3" data-label="Name">{row.name || 'Untitled'}</td>
                    <td className="px-4 py-3" data-label="Contact">{row.contactName}</td>
                    <td className="px-4 py-3" data-label="Email">{row.contactEmail}</td>
                    <td className="px-4 py-3 text-right" data-label=""><span className="inline-flex gap-2"><Button variant="outline" onClick={(event) => { event.stopPropagation(); setSupplierEdit({ index, draft: { ...row } }); }}>Edit</Button><Button variant="destructive" onClick={(event) => { event.stopPropagation(); setSupplierTemplates((rows) => rows.filter((_, i) => i !== index)); }}>Delete</Button></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!supplierTemplates.length ? <p className="px-4 py-6 text-sm text-gray-500">No supplier details yet.</p> : null}
          </div>
          <Button className="w-fit" onClick={() => void save()}>Save supplier details</Button>
        </div>
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Email templates</h2>
            <Button variant="outline" onClick={() => setEmailEdit({ index: null, draft: { id: crypto.randomUUID(), name: '', kind: 'invoice', subject: '', body: '' } })}>Add template</Button>
          </div>
          <p className="text-sm text-gray-500">Use {'{{number}}'}, {'{{customer}}'}, {'{{title}}'}, and {'{{po}}'}. {'{{po}}'} becomes PO: 1BT-35, or POs: 1BT-35, 1BT-36 when an invoice has more than one.</p>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3 text-right"><ColumnFilterToggle open={emailSearch.open} onClick={emailSearch.toggle} /></th></tr>
                {emailSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Name' }, { key: 'type', label: 'Type' }, { key: 'subject', label: 'Subject' }]} draft={emailSearch.draft} onChange={emailSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {emailTemplates.map((row, index) => ({ row, index })).filter(({ row }) => emailSearch.matches({ name: row.name || 'Untitled', type: row.kind === 'purchase_order' ? 'Purchase order' : row.kind === 'credit_note' ? 'Credit note' : row.kind[0].toUpperCase() + row.kind.slice(1), subject: row.subject })).map(({ row, index }) => (
                  <tr key={row.id} className="cursor-pointer border-t border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800" onClick={() => setEmailEdit({ index, draft: { ...row } })}>
                    <td className="px-4 py-3" data-label="Name">{row.name || 'Untitled'}</td>
                    <td className="px-4 py-3" data-label="Type">{row.kind === 'purchase_order' ? 'Purchase order' : row.kind === 'credit_note' ? 'Credit note' : row.kind[0].toUpperCase() + row.kind.slice(1)}</td>
                    <td className="px-4 py-3" data-label="Subject">{row.subject}</td>
                    <td className="px-4 py-3 text-right" data-label=""><span className="inline-flex gap-2"><Button variant="outline" onClick={(event) => { event.stopPropagation(); setEmailEdit({ index, draft: { ...row } }); }}>Edit</Button><Button variant="destructive" onClick={(event) => { event.stopPropagation(); setEmailTemplates((rows) => rows.filter((_, i) => i !== index)); }}>Delete</Button></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!emailTemplates.length ? <p className="px-4 py-6 text-sm text-gray-500">No email templates yet.</p> : null}
          </div>
          <Button className="w-fit" onClick={() => void save()}>Save email templates</Button>
        </div>
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Terms templates</h2>
            <Button variant="outline" onClick={() => setTermsEdit({ index: null, draft: { id: crypto.randomUUID(), name: '', kind: 'invoice', paymentTerms: '', delivery: '', notice: '', acceptance: '', validDays: 0 } })}>Add template</Button>
          </div>
          <p className="text-sm text-gray-500">One for an invoice, one for an offer, and one for a purchase order we raise. A new document copies the first of its kind. On the form, Terms template fills the commercial fields, which stay editable.</p>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Payment terms</th><th className="px-4 py-3 text-right"><ColumnFilterToggle open={termsSearch.open} onClick={termsSearch.toggle} /></th></tr>
                {termsSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Name' }, { key: 'type', label: 'Type' }, { key: 'terms', label: 'Payment terms' }]} draft={termsSearch.draft} onChange={termsSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {termsTemplates.map((row, index) => ({ row, index })).filter(({ row }) => termsSearch.matches({ name: row.name || 'Untitled', type: row.kind === 'purchase_order' ? 'Purchase order' : row.kind === 'offer' ? 'Offer' : 'Invoice', terms: row.paymentTerms })).map(({ row, index }) => (
                  <tr key={row.id} className="cursor-pointer border-t border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800" onClick={() => setTermsEdit({ index, draft: { ...row } })}>
                    <td className="px-4 py-3" data-label="Name">{row.name || 'Untitled'}</td>
                    <td className="px-4 py-3" data-label="Type">{row.kind === 'purchase_order' ? 'Purchase order' : row.kind === 'offer' ? 'Offer' : 'Invoice'}</td>
                    <td className="px-4 py-3" data-label="Payment terms">{row.paymentTerms}</td>
                    <td className="px-4 py-3 text-right" data-label=""><span className="inline-flex gap-2"><Button variant="outline" onClick={(event) => { event.stopPropagation(); setTermsEdit({ index, draft: { ...row } }); }}>Edit</Button><Button variant="destructive" onClick={(event) => { event.stopPropagation(); setTermsTemplates((rows) => rows.filter((_, i) => i !== index)); }}>Delete</Button></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!termsTemplates.length ? <p className="px-4 py-6 text-sm text-gray-500">No terms templates yet.</p> : null}
          </div>
          <Button className="w-fit" onClick={() => void save()}>Save terms templates</Button>
        </div>
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Bank details</h2>
            <Button variant="outline" onClick={() => setBankEdit({ index: null, draft: emptyBankAccount() })}>Add bank details</Button>
          </div>
          <p className="text-sm text-gray-500">The default account is the domestic one and is selected on a new invoice. Add another account for payments abroad, including SWIFT/BIC. An invoice can use any of these.</p>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Bank</th><th className="px-4 py-3">Default</th><th className="px-4 py-3 text-right"><ColumnFilterToggle open={bankSearch.open} onClick={bankSearch.toggle} /></th></tr>
                {bankSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Name' }, { key: 'bank', label: 'Bank' }, { key: 'domestic', label: 'Default' }]} draft={bankSearch.draft} onChange={bankSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {bankAccounts.map((account, index) => ({ account, index })).filter(({ account }) => bankSearch.matches({ name: account.name || 'Untitled', bank: account.bankName, domestic: account.domestic ? 'Yes' : '' })).map(({ account, index }) => (
                  <tr key={account.id} className="cursor-pointer border-t border-gray-200 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800" onClick={() => setBankEdit({ index, draft: { ...account } })}>
                    <td className="px-4 py-3" data-label="Name">{account.name || 'Untitled'}</td>
                    <td className="px-4 py-3" data-label="Bank">{account.bankName}</td>
                    <td className="px-4 py-3" data-label="Default">{account.domestic ? 'Yes' : ''}</td>
                    <td className="px-4 py-3 text-right" data-label=""><span className="inline-flex gap-2"><Button variant="outline" onClick={(event) => { event.stopPropagation(); setBankEdit({ index, draft: { ...account } }); }}>Edit</Button><Button variant="destructive" onClick={(event) => { event.stopPropagation(); setBankAccounts((rows) => { const next = rows.filter((_, i) => i !== index); if (next.length && !next.some((item) => item.domestic)) next[0] = { ...next[0], domestic: true }; return next; }); }}>Delete</Button></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!bankAccounts.length ? <p className="px-4 py-6 text-sm text-gray-500">No bank details yet.</p> : null}
          </div>
          <Button className="w-fit" onClick={() => void save()}>Save bank details</Button>
        </div>
        </div>
      ) : null}
      {tab === 'people' ? (
        <div>
          <p className="mb-4 text-sm text-gray-500">People sign in with Microsoft. Add their work email to a group. Admin can do everything, operator can run day-to-day finance, viewer can read.</p>
          <div className="mb-4 grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((group, index) => (
            <div key={group.name} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="mb-3 font-semibold">{group.name}</div>
              <Field label="Role">
                <Select value={group.role} onChange={(event) => setGroups((rows) => rows.map((item, i) => i === index ? { ...item, role: event.target.value } : item))}>
                  <option value="admin">Admin</option>
                  <option value="operator">Operator</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </Field>
              <div className="mt-4"><ChipList values={group.members} placeholder="name@maxiron.com" onChange={(members) => setGroups((rows) => rows.map((item, i) => i === index ? { ...item, members } : item))} /></div>
            </div>
          ))}
          </div>
          <Button onClick={() => void notice.run('Saving users', async () => { await apiPut('/api/admin/groups', { groups }); reload(); }, 'Users saved', { confirm: false })}>Save users</Button>
        </div>
      ) : null}
      {tab === 'sharepoint' ? (
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-2 text-lg font-semibold">Site</h3>
            <p className="mb-4 text-sm text-gray-500">Customer files live in MaxIronFiles. Paste the site URL up to the site name.</p>
            <Field label="Site URL"><Input value={sharePointSiteUrl} placeholder="https://maxiron55.sharepoint.com/sites/MaxIronFiles" onChange={(e) => setSharePointSiteUrl(e.target.value)} /></Field>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void notice.run('Looking up the SharePoint site', async () => {
                const result = await apiPost<{ siteId?: string; siteUrl?: string }>('/api/settings/sharepoint/lookup', { url: sharePointSiteUrl });
                if (result.siteUrl) setSharePointSiteUrl(result.siteUrl);
                if (result.siteId) setSharePointSiteId(result.siteId);
                return result;
              }, (result) => result.siteId ? 'Site found. Save SharePoint to keep it.' : 'URL parsed. Graph could not return a site id yet; save the URL anyway.')}>Look up site</Button>
              <Button size="sm" onClick={() => void notice.run('Syncing purchase orders', () => apiPost<{ created?: number; updated?: number }>('/api/integrations/sharepoint/pull-pos'), (result) => `SharePoint POs: ${result.created ?? 0} created, ${result.updated ?? 0} updated`)}>Sync purchase orders</Button>
              {sharePointSiteId ? <span className="self-center text-xs text-emerald-600">Site id saved</span> : null}
            </div>
            <SyncFromField label="Purchase orders synced from" hint={syncHint} value={syncFrom.sharepoint} onSave={(at) => saveSyncTime('sharepoint', at)} />
            <div className="mt-4"><Field label="Document library"><Input value={sharePointDriveName} onChange={(e) => setSharePointDriveName(e.target.value)} /></Field></div>
            <div className="mt-3"><Field label="Customers root"><Input value={sharePointBasePath} onChange={(e) => setSharePointBasePath(e.target.value)} /></Field></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-2 text-lg font-semibold">Folders under each customer</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {FOLDER_LABELS.map((item) => (
                <Field key={item.key} label={item.label}><Input value={folders[item.key]} onChange={(event) => setFolders((current) => ({ ...current, [item.key]: event.target.value }))} /></Field>
              ))}
            </div>
            <div className="mt-4 rounded-lg bg-gray-100 p-3 text-xs dark:bg-gray-800">
              <div className="mb-1 font-semibold">RWG example</div>
              <div>POs: {preview('purchaseOrders')}</div>
              <div>Invoices: {preview('invoices')}</div>
              <div>Quotes: {preview('quotations')}</div>
              <div>Contract: {preview('contracts')}</div>
              <div>NDA: {preview('nda')}</div>
              <div>Licences: {preview('licences')}</div>
            </div>
          </div>
          <div className="xl:col-span-2"><Button onClick={() => void save()}>Save SharePoint</Button></div>
        </div>
      ) : null}
      {tab === 'integrations' ? (
        <div className="grid gap-4">
          <p className="text-sm text-[var(--bo-muted)]">Each service is one card. Clear an address to use the built-in one. Leave a key blank to keep the key already in use. A saved key is not shown again.</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {CONNECTION_ORDER.filter((key) => key === 'crunch' || key in connections).map((key) => {
              const ok = key === 'crunch' ? crunch.connected : Boolean(connections[key]);
              const label = connectionLabel(key, accountingAdapter);
              const opens = key === 'crunch' ? 'accounting' : key === 'sharepoint' ? 'sharepoint' : '';
              const body = (
                <>
                  <div className="text-sm font-medium">{label}</div>
                  <div className={`mt-1 font-semibold ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>{ok ? 'Connected' : key === 'crunch' ? 'Not connected' : 'Not configured'}</div>
                  {key === 'crunch' && crunch.connected ? <div className="mt-1 text-sm text-gray-500">{crunch.companyName || crunch.userName || crunch.email || ''}</div> : null}
                </>
              );
              const className = 'rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm dark:border-gray-800 dark:bg-gray-900';
              return opens ? (
                <button key={key} type="button" className={className} onClick={() => setTab(key === 'crunch' ? 'accounting' : 'sharepoint')}>{body}</button>
              ) : (
                <div key={key} className={className}>{body}</div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="text-lg font-semibold">Cursor</h3>
            {endpointFields('Cursor')}
            {secretFields('Cursor')}
            <Button className="w-fit" onClick={() => void saveConnection()}>Save Cursor</Button>
          </div>
          <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="text-lg font-semibold">Clockify</h3>
            {endpointFields('Clockify')}
            {secretFields('Clockify')}
            <Field label="Workspace ID"><Input value={clockifyWorkspaceId} onChange={(e) => setClockifyWorkspaceId(e.target.value)} /></Field>
            <Field label="Region">
              <Select value={clockifyRegion} onChange={(e) => setClockifyRegion(e.target.value)}>
                <option value="euw2">UK (euw2)</option>
                <option value="euc1">EU (euc1)</option>
                <option value="use2">USA (use2)</option>
                <option value="apse2">AU (apse2)</option>
                <option value="global">Global</option>
              </Select>
            </Field>
            <SyncFromField label="Hours synced from" hint={syncHint} value={syncFrom.clockify} onSave={(at) => saveSyncTime('clockify', at)} />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void saveService()}>Save Clockify</Button>
              <Button variant="outline" onClick={() => void notice.run('Syncing Clockify', () => apiPost<{ pulled?: number; peopleCreated?: number }>('/api/integrations/clockify/pull'), (result) => `Clockify pulled ${result.pulled ?? 0} entries, created ${result.peopleCreated ?? 0} people`)}>Sync now</Button>
            </div>
          </div>
          <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="text-lg font-semibold">GitHub</h3>
            {endpointFields('GitHub')}
            {secretFields('GitHub')}
            <Field label="Repo"><Input value={githubRepo} onChange={(e) => setGithubRepo(e.target.value)} /></Field>
            <Button className="w-fit" onClick={() => void saveService()}>Save GitHub</Button>
          </div>
          <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="text-lg font-semibold">HubSpot</h3>
            <p className="text-sm text-gray-500">Companies, deals, and leads changed since this time.</p>
            {endpointFields('HubSpot')}
            {secretFields('HubSpot')}
            <SyncFromField label="Synced from" hint={syncHint} value={syncFrom.hubspot} onSave={(at) => saveSyncTime('hubspot', at)} />
            <Button className="w-fit" onClick={() => void saveConnection()}>Save HubSpot</Button>
          </div>
          <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="text-lg font-semibold">Microsoft</h3>
            <p className="text-sm text-gray-500">Inbox watches office@maxiron.com and accounts@maxiron.com.</p>
            {endpointFields('Microsoft')}
            {secretFields('Microsoft')}
            <Button className="w-fit" onClick={() => void saveConnection()}>Save Microsoft</Button>
          </div>
          </div>
        </div>
      ) : null}
      {tab === 'accounting' ? (
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-1 text-lg font-semibold">Accounting system</h3>
            <p className="mb-4 text-sm text-gray-500">Books calls use the installed adapter.</p>
            <Field label="Accounting system">
              <Select value={accountingAdapter} onChange={(event) => setAccountingAdapter(event.target.value)}>
                <option value="crunch">Crunch</option>
              </Select>
            </Field>
            <Button className="mt-4" onClick={() => void save()}>Save accounting system</Button>
          </div>
          {accountingAdapter === 'crunch' ? (
            <div className="grid gap-5 rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div>
                <h3 className="text-lg font-semibold">Crunch</h3>
                <p className="mt-1 text-sm text-gray-500">Addresses, client id, and the company connection for the Crunch adapter. This does not approve Crunch payments.</p>
              </div>
              {endpoints.filter((row) => row.group === 'Crunch').map((row) => (
                <Field key={row.id} label={row.label}>
                  <Input value={endpointDraft[row.id] ?? row.value} autoComplete="off" onChange={(event) => setEndpointDraft((current) => ({ ...current, [row.id]: event.target.value }))} />
                </Field>
              ))}
              <Field label="Client id"><Input value={crunchClientId} autoComplete="off" onChange={(event) => setCrunchClientId(event.target.value)} /></Field>
              {secretStatus.filter((row) => row.id === 'crunch').map((row) => (
                <Field key={row.id} label={row.label}>
                  <Input type="password" autoComplete="new-password" value={secretDraft[row.id] || ''} placeholder={row.source === 'none' ? 'Not set' : 'Leave blank to keep the current key'} onChange={(event) => setSecretDraft((current) => ({ ...current, [row.id]: event.target.value }))} />
                  <p className="mt-1 text-xs text-[var(--bo-faint)]">{row.source === 'settings' ? 'Saved in settings' : row.source === 'server' ? 'Using the server key' : 'Not set'}</p>
                </Field>
              ))}
              <Button className="w-fit" onClick={() => void notice.run('Saving Crunch connection', async () => { const result = await apiPost<Parameters<typeof apply>[0]>('/api/integrations/secrets', { secrets: secretDraft, endpoints: endpointDraft, crunchClientId }); apply(result); }, 'Crunch connection saved')}>Save Crunch connection</Button>
              {crunch.yearStart && crunch.yearEnd ? <p className="text-sm text-gray-500">Crunch year {crunch.periodName || ''}: {crunch.yearStart} to {crunch.yearEnd}</p> : (
                <div className="grid gap-3">
                  <Field label="Year start"><Input type="date" value={yearStart} onChange={(e) => setYearStart(e.target.value)} /></Field>
                  <Field label="Year end"><Input type="date" value={yearEnd} onChange={(e) => setYearEnd(e.target.value)} /></Field>
                  <Button className="w-fit" variant="outline" onClick={() => void save()}>Save year</Button>
                </div>
              )}
              {!crunch.clientConfigured ? <div className="font-semibold text-amber-600">Client credentials missing on the API</div> : crunch.connected ? (
                <div>
                  <div className="font-semibold text-emerald-600">Connected</div>
                  <div className="mt-2 text-sm">{crunch.userName || crunch.email || 'Crunch user'}</div>
                  {crunch.companyName ? <div className="text-sm text-gray-500">{crunch.companyName}</div> : null}
                  {crunch.lastError ? <div className="mt-2 text-sm text-amber-600">{crunch.lastError}</div> : null}
                  <SyncFromField label="Payments synced from" hint={syncHint} value={crunch.lastSyncAt} onSave={(at) => saveSyncTime('crunch', at)} />
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => void notice.run('Refreshing Crunch', async () => { setCrunch(await apiPost<CrunchStatus>('/api/integrations/crunch/probe')); }, 'Crunch identity refreshed')}>Refresh identity</Button>
                    <Button size="sm" variant="outline" onClick={() => void notice.run('Syncing Crunch', async () => { const result = await apiPost<{ crunch?: CrunchStatus }>('/api/integrations/crunch/sync'); setCrunch(result.crunch || result as CrunchStatus); reload(); }, 'Crunch outbound sync finished')}>Sync now</Button>
                    <Button size="sm" variant="outline" onClick={() => void notice.run('Disconnecting Crunch', async () => { const result = await apiPost<{ crunch?: CrunchStatus }>('/api/integrations/crunch/disconnect'); setCrunch(result.crunch || { clientConfigured: crunch.clientConfigured, connected: false }); reload(); }, 'Crunch disconnected')}>Disconnect</Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-amber-600">Not connected</div>
                  <Button className="mt-4" onClick={() => { window.location.assign('/api/integrations/crunch/connect'); }}>Connect Crunch</Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
      {tab === 'jobs' ? (
        <div>
          <Input className="mb-3 h-8 max-w-xs" aria-label="Search jobs" placeholder="Search jobs" value={jobSearch} onChange={(event) => setJobSearch(event.target.value)} />
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="bo-cards min-w-full text-sm">
              <thead><tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                <SortHeader className="px-3 py-2 font-medium" label="Job" active={jobSort?.key === 'name'} dir={jobSort?.dir || 'asc'} onSort={() => setJobSort((current) => nextSort(current, 'name'))} />
                <SortHeader className="py-2 font-medium" label="Cadence" active={jobSort?.key === 'cadence'} dir={jobSort?.dir || 'asc'} onSort={() => setJobSort((current) => nextSort(current, 'cadence'))} />
                <SortHeader className="py-2 font-medium" label="Enabled" active={jobSort?.key === 'enabled'} dir={jobSort?.dir || 'asc'} onSort={() => setJobSort((current) => nextSort(current, 'enabled'))} />
                <th className="w-8 pr-2 text-right"><ColumnFilterToggle open={jobColumnSearch.open} onClick={jobColumnSearch.toggle} /></th>
              </tr>
              {jobColumnSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Job' }, { key: 'cadence', label: 'Cadence' }, { key: 'enabled', label: 'Enabled' }]} draft={jobColumnSearch.draft} onChange={jobColumnSearch.set} trailing /> : null}
              </thead>
              <tbody>
                {applyTableQuery(jobs, jobSearch, [
                  { key: 'name', text: (job) => job.name },
                  { key: 'cadence', text: (job) => job.cadence },
                  { key: 'enabled', text: (job) => job.enabled ? 'Yes' : 'No', sort: (job) => job.enabled ? 1 : 0 },
                ], jobSort).filter((job) => jobColumnSearch.matches({ name: job.name, cadence: job.cadence, enabled: job.enabled ? 'Yes' : 'No' })).map((job) => (
                  <tr key={job.key}>
                    <td className="px-3 py-2" data-label="Job">{job.name}</td>
                    <td data-label="Cadence">{job.cadence}</td>
                    <td data-label="Enabled"><input type="checkbox" checked={job.enabled} onChange={(event) => setJobs((rows) => rows.map((item) => item.key === job.key ? { ...item, enabled: event.target.checked } : item))} /></td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button className="mt-4" onClick={() => void save()}>Save jobs</Button>
        </div>
      ) : null}
      {supplierEdit ? (
        <Dialog title={supplierEdit.index == null ? 'Add supplier details' : 'Edit supplier details'} onClose={() => setSupplierEdit(null)} footer={
          <>
            {supplierEdit.index != null ? <Button variant="outline" onClick={() => { setSupplierTemplates((rows) => rows.filter((_, i) => i !== supplierEdit.index)); setSupplierEdit(null); }}>Remove</Button> : null}
            <Button variant="outline" onClick={() => setSupplierEdit(null)}>Cancel</Button>
            <Button onClick={() => {
              const draft = supplierEdit.draft;
              setSupplierTemplates((rows) => supplierEdit.index == null ? [...rows, draft] : rows.map((item, i) => i === supplierEdit.index ? draft : item));
              setSupplierEdit(null);
            }}>OK</Button>
          </>
        }>
          <Field label="Template name"><Input value={supplierEdit.draft.name} onChange={(event) => setSupplierEdit({ ...supplierEdit, draft: { ...supplierEdit.draft, name: event.target.value } })} /></Field>
          <Field label="Contact"><Input value={supplierEdit.draft.contactName} onChange={(event) => setSupplierEdit({ ...supplierEdit, draft: { ...supplierEdit.draft, contactName: event.target.value } })} /></Field>
          <Field label="Role"><Input value={supplierEdit.draft.contactRole} onChange={(event) => setSupplierEdit({ ...supplierEdit, draft: { ...supplierEdit.draft, contactRole: event.target.value } })} /></Field>
          <Field label="Email"><Input value={supplierEdit.draft.contactEmail} onChange={(event) => setSupplierEdit({ ...supplierEdit, draft: { ...supplierEdit.draft, contactEmail: event.target.value } })} /></Field>
          <Field label="Telephone"><Input value={supplierEdit.draft.contactPhone} onChange={(event) => setSupplierEdit({ ...supplierEdit, draft: { ...supplierEdit.draft, contactPhone: event.target.value } })} /></Field>
        </Dialog>
      ) : null}
      {emailEdit ? (
        <Dialog title={emailEdit.index == null ? 'Add email template' : 'Edit email template'} onClose={() => setEmailEdit(null)} footer={
          <>
            {emailEdit.index != null ? <Button variant="outline" onClick={() => { setEmailTemplates((rows) => rows.filter((_, i) => i !== emailEdit.index)); setEmailEdit(null); }}>Remove</Button> : null}
            <Button variant="outline" onClick={() => setEmailEdit(null)}>Cancel</Button>
            <Button onClick={() => {
              const draft = emailEdit.draft;
              setEmailTemplates((rows) => emailEdit.index == null ? [...rows, draft] : rows.map((item, i) => i === emailEdit.index ? draft : item));
              setEmailEdit(null);
            }}>OK</Button>
          </>
        }>
          <Field label="Name"><Input value={emailEdit.draft.name} onChange={(event) => setEmailEdit({ ...emailEdit, draft: { ...emailEdit.draft, name: event.target.value } })} /></Field>
          <Field label="Type">
            <Select value={emailEdit.draft.kind} onChange={(event) => setEmailEdit({ ...emailEdit, draft: { ...emailEdit.draft, kind: event.target.value as EmailTemplateKind } })}>
              <option value="proposal">Proposal</option>
              <option value="quotation">Quotation</option>
              <option value="invoice">Invoice</option>
              <option value="credit_note">Credit note</option>
              <option value="purchase_order">Purchase order</option>
            </Select>
          </Field>
          <Field label="Subject"><Input value={emailEdit.draft.subject} onChange={(event) => setEmailEdit({ ...emailEdit, draft: { ...emailEdit.draft, subject: event.target.value } })} /></Field>
          <Field label="Message"><TextArea value={emailEdit.draft.body} onChange={(event) => setEmailEdit({ ...emailEdit, draft: { ...emailEdit.draft, body: event.target.value } })} /></Field>
        </Dialog>
      ) : null}
      {termsEdit ? (
        <Dialog title={termsEdit.index == null ? 'Add terms template' : 'Edit terms template'} onClose={() => setTermsEdit(null)} footer={
          <>
            {termsEdit.index != null ? <Button variant="outline" onClick={() => { setTermsTemplates((rows) => rows.filter((_, i) => i !== termsEdit.index)); setTermsEdit(null); }}>Remove</Button> : null}
            <Button variant="outline" onClick={() => setTermsEdit(null)}>Cancel</Button>
            <Button onClick={() => {
              const draft = termsEdit.draft;
              setTermsTemplates((rows) => termsEdit.index == null ? [...rows, draft] : rows.map((item, i) => i === termsEdit.index ? draft : item));
              setTermsEdit(null);
            }}>OK</Button>
          </>
        }>
          <Field label="Name"><Input value={termsEdit.draft.name} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, name: event.target.value } })} /></Field>
          <Field label="Type">
            <Select value={termsEdit.draft.kind} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, kind: event.target.value as TermsTemplateKind } })}>
              <option value="invoice">Invoice</option>
              <option value="offer">Offer</option>
              <option value="purchase_order">Purchase order</option>
            </Select>
          </Field>
          <Field label="Valid days"><Input type="number" value={termsEdit.draft.validDays || ''} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, validDays: Number(event.target.value) || 0 } })} /></Field>
          <Field label="Payment terms"><TextArea value={termsEdit.draft.paymentTerms} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, paymentTerms: event.target.value } })} /></Field>
          <Field label="Delivery"><TextArea value={termsEdit.draft.delivery} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, delivery: event.target.value } })} /></Field>
          <Field label="Notice / Terms"><TextArea value={termsEdit.draft.notice} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, notice: event.target.value } })} /></Field>
          <Field label="To proceed"><TextArea value={termsEdit.draft.acceptance} onChange={(event) => setTermsEdit({ ...termsEdit, draft: { ...termsEdit.draft, acceptance: event.target.value } })} /></Field>
        </Dialog>
      ) : null}
      {bankEdit ? (
        <Dialog title={bankEdit.index == null ? 'Add bank details' : 'Edit bank details'} onClose={() => setBankEdit(null)} footer={
          <>
            {bankEdit.index != null ? <Button variant="outline" onClick={() => {
              setBankAccounts((rows) => {
                const next = rows.filter((_, i) => i !== bankEdit.index);
                if (next.length && !next.some((row) => row.domestic)) next[0] = { ...next[0], domestic: true };
                return next;
              });
              setBankEdit(null);
            }}>Remove</Button> : null}
            <Button variant="outline" onClick={() => setBankEdit(null)}>Cancel</Button>
            <Button onClick={() => {
              const draft = bankEdit.draft;
              setBankAccounts((rows) => {
                let next = bankEdit.index == null ? [...rows, draft] : rows.map((item, i) => i === bankEdit.index ? draft : item);
                if (draft.domestic) next = next.map((item) => ({ ...item, domestic: item.id === draft.id }));
                if (next.length && !next.some((item) => item.domestic)) next[0] = { ...next[0], domestic: true };
                return next;
              });
              setBankEdit(null);
            }}>OK</Button>
          </>
        }>
          <Field label="Name"><Input value={bankEdit.draft.name} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, name: event.target.value } })} /></Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bankEdit.draft.domestic} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, domestic: event.target.checked } })} />
            Default
          </label>
          <Field label="Bank"><Input value={bankEdit.draft.bankName} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, bankName: event.target.value } })} /></Field>
          <Field label="Account name"><Input value={bankEdit.draft.accountName} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, accountName: event.target.value } })} /></Field>
          <Field label="Bank address"><TextArea value={bankEdit.draft.bankAddress} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, bankAddress: event.target.value } })} /></Field>
          <Field label="Account number"><Input value={bankEdit.draft.accountNumber} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, accountNumber: event.target.value } })} /></Field>
          <Field label="Sort code"><Input value={bankEdit.draft.sortCode} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, sortCode: event.target.value } })} /></Field>
          <Field label="IBAN"><Input value={bankEdit.draft.iban} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, iban: event.target.value } })} /></Field>
          <Field label="SWIFT/BIC"><Input value={bankEdit.draft.swiftBic} onChange={(event) => setBankEdit({ ...bankEdit, draft: { ...bankEdit.draft, swiftBic: event.target.value } })} /></Field>
        </Dialog>
      ) : null}
    </div>
  );
}
